import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, Button, CardBody, Collapse, FormGroup, Input, Label } from "reactstrap";
import ModalComponent from "../../misc/ModalComponent";
import Loading from "../../misc/Loading";
import CoreApiError from "../../misc/CoreApiError";
import { useDispatch, useSelector } from "react-redux";
import { EmptyRequestState, RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import StatusCode from "../../../util/StatusCode";
import { CoreReservedValuesImportPreview } from "../../../models/CoreInstanceSettings";
import { ClearPreviewReservedValuesUpload, ClearUploadReservedValues, downloadReservedValues, GetReservedValuesCount, previewReservedValuesUpload, uploadReservedValues } from "../../../store/InstanceConfigAction";
import { useTranslation } from 'react-i18next';

interface ReservedValuesProps {
    hasEditAccess: boolean
}

const ReservedValues = (props: ReservedValuesProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const dispatch = useDispatch();
    const [showReservedValues, setShowReservedValues] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined);
    const [hasUploaded, setHasUploaded] = useState<boolean>(false);

    const reservedValuesCount = useSelector<RootState, RequestState<number>>(state => state.instanceSettings.reservedValuesCount);
    const reservedValuesUploadPreview = useSelector<RootState, RequestState<CoreReservedValuesImportPreview>>(state => state.instanceSettings.reservedValuesUploadPreview);
    const reservedValuesUpload = useSelector<RootState, RequestState<CoreReservedValuesImportPreview>>(state => state.instanceSettings.reservedValuesUpload);
    const reservedValuesDownload = useSelector<RootState, EmptyRequestState>(state => state.instanceSettings.reservedValuesDownload);

    useEffect(() => {
        GetReservedValuesCount()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (reservedValuesUpload.code === StatusCode.COMPLETE) {
            GetReservedValuesCount()(dispatch);
        }
    }, [dispatch, reservedValuesUpload.code]);

    const closeModal = () => {
        setOpenModal(false);
        setSelectedFile(undefined);
        setHasUploaded(false);
        ClearPreviewReservedValuesUpload()(dispatch);
        ClearUploadReservedValues()(dispatch);
    }

    const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        setHasUploaded(false);
        setSelectedFile(file);

        ClearPreviewReservedValuesUpload()(dispatch);
        ClearUploadReservedValues()(dispatch);

        if (file) {
            previewReservedValuesUpload(file)(dispatch);
        }
    }

    const onUpload: React.MouseEventHandler<HTMLButtonElement> = (_) => {
        if (!selectedFile) {
            return;
        }

        setHasUploaded(true);
        uploadReservedValues(selectedFile)(dispatch);
    }

    const onDownload = () => {
        downloadReservedValues()(dispatch);
    }

    const preview = useMemo(() => {
        if (reservedValuesUpload.code === StatusCode.COMPLETE) {
            return reservedValuesUpload.data;
        }

        if (reservedValuesUploadPreview.code === StatusCode.COMPLETE) {
            return reservedValuesUploadPreview.data;
        }

        return undefined;
    }, [reservedValuesUpload, reservedValuesUploadPreview]);

    const hasPreviewErrors = (preview?.totalErrorCount ?? 0) > 0;

    const uploadReservedValuesModal = () => {
        return (
            <ModalComponent
                header={t('reservedValues.modal.header', { ns: 'configuration' })}
                cancel={t('action.close', { ns: 'common' })}
                confirm={t('reservedValues.modal.confirm', { ns: 'configuration' })}
                disabled={!selectedFile || reservedValuesUploadPreview.code !== StatusCode.COMPLETE || reservedValuesUpload.code === StatusCode.PENDING || hasPreviewErrors}
                size="lg"
                isOpen={openModal}
                toggleModal={closeModal}
                update={onUpload}
                saveStatus={reservedValuesUpload.code}
                successMsg={t('reservedValues.modal.success', { ns: 'configuration' })}
                errorMsg={t('reservedValues.modal.error', { ns: 'configuration' })}
            >
                <FormGroup>
                    <Label for="reservedValuesFile">{t('reservedValues.modal.fileType', { ns: 'configuration' })}</Label>
                    <Input id="reservedValuesFile" type="file" accept=".csv,text/csv" onChange={onFileChange} />
                </FormGroup>

                {selectedFile && <div className="reserved-values"><span>{t('reservedValues.modal.selectedFile', { ns: 'configuration' })}</span><span> {selectedFile.name}</span></div>}

                {reservedValuesUploadPreview.code === StatusCode.PENDING && <Loading />}
                {reservedValuesUploadPreview.code === StatusCode.ERROR && <CoreApiError requestState={reservedValuesUploadPreview} />}
                {hasUploaded && reservedValuesUpload.code === StatusCode.ERROR && <CoreApiError requestState={reservedValuesUpload} />}

                {preview && <>
                    <div className="reserved-values"><span>{t('reservedValues.modal.currentValuesCount', { ns: 'configuration' })}</span><span> {preview.existingCount}</span></div>
                    <div className="reserved-values"><span>{t('reservedValues.modal.valuesInFileCount', { ns: 'configuration' })}</span><span>{preview.uploadedCount}</span></div>
                    <div className="reserved-values"><span>{t('reservedValues.modal.addedValuesCount', { ns: 'configuration' })}</span><span> {preview.addedCount}</span></div>
                    <div className="reserved-values"><span>{t('reservedValues.modal.removedValuesCount', { ns: 'configuration' })}</span><span>{preview.removedCount}</span></div>
                    <div className="reserved-values"><span>{t('reservedValues.modal.updatedValuesCount', { ns: 'configuration' })}</span><span>{preview.updatedCount}</span></div>

                    {preview.totalErrorCount > 0 && <div className="alert alert-danger mt-3 mb-0">
                        <p className="mb-2">{t('reservedValues.modal.fileError', { ns: 'configuration', count: preview.totalErrorCount })} {preview.totalErrorCount > 10 && t('reservedValues.modal.fileErrorShowTenFirst', { ns: 'configuration' })} {t('reservedValues.modal.fileErrorCorrect', { ns: 'configuration' })}</p>
                        <ul className="mb-0">
                            {preview.errors.map((error, index) =>
                                <li key={`${error.rowNumber}-${index}`}>
                                    {t('reservedValues.modal.row', { ns: 'configuration' })} {error.rowNumber}: {t(`reservedValues.modal.invalid.${error.message}`, { ns: 'configuration', missingHeaders: error?.missingHeaders })}</li>)}
                        </ul>
                    </div>}
                </>
                }
            </ModalComponent>)
    }

    return (<>
        <Card className="mt-3">
            <CardHeader tag="h5">
                <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setShowReservedValues(!showReservedValues)}>{showReservedValues ? '-' : '+'}</Button>
                {t('reservedValues.card.title', { ns: 'configuration' })}
            </CardHeader>

            <Collapse isOpen={showReservedValues}>
                <CardBody>
                    <p>{t('reservedValues.card.count', { ns: 'configuration' })}
                        {reservedValuesCount.code === StatusCode.COMPLETE ? reservedValuesCount.data.toLocaleString('sv-SE') : '-'}</p>
                    <Button className="me-3" disabled={reservedValuesDownload.code === StatusCode.PENDING || !props.hasEditAccess} onClick={onDownload}>{t('reservedValues.card.downLoad', { ns: 'configuration' })}</Button>
                    <Button disabled={!props.hasEditAccess} onClick={() => setOpenModal(true)}>{t('reservedValues.card.upLoad', { ns: 'configuration' })}</Button>
                </CardBody>
            </Collapse>

        </Card>

        {uploadReservedValuesModal()}

    </>)
}

export default ReservedValues;
